import { BaseZenputModel } from '../../../backbone-unorganized/shared/forms';
import { BluetoothErrorCode } from '../../../backbone-unorganized/utils/bluetooth';
import { CordovaPlugins } from '../../../shared/CordovaPlugins';

export const BluetoothDeviceAPI = BaseZenputModel.extend({
    CONNECT_TIMEOUT_MS: 3000,
    DISCOVERY_DELAY_MS: 1000,

    // Response structure (discovery) looks like:

    //     [] services
    //         uuid
    //         [] characteristics
    //             uuid
    //             [] descriptors
    //                 uuid
    //                 {} permissions
    //             {} permissions
    //             {} properties

    // BluetoothLE periphereal devices contain 1 or more services, for example, a temperature service or location
    // service identified by a UUID. Each service contains characteristics, for example, a temperature reading in
    // floating point identified by a UUID. These constants represent the UUIDs needed for discovery of
    // Cooper Atkins/Bluetherm BLE temperature services and subscription to temperature characteristics.
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: null,
        BLE_TEMPERATURE_CHARACTERISTIC: null,
        BLE_TEMPERATURE_CHARACTERISTIC_NOTIF: null
    },

    _active_address: null,

    defaults: {}, // no data to save for this, just properties and functions

    /**
     * Normally we would take attributes first, but we aren't using any.
     * Modify our constructor to only take options.
     */
    constructor: function (options) {
        options = options || {};
        $.extend(this, options);
        BluetoothDeviceAPI.__super__.constructor.call(this, [], options);
    },

    /**
     * Event callbacks.
     */
    on_pre_connect: function (callback) {},
    on_device_connected: function () {},
    on_device_connection_failure: function () {},
    on_device_disconnected: function (error_code) {},
    on_reading_received: function (value) {},
    on_button_pressed: function (value) {},

    _get_connect_fn: function (callback) {
        var this_ = this;

        CordovaPlugins().bluetoothle.wasConnected(
            function (response) {
                callback(response.wasConnected ? 'reconnect' : 'connect');
            },
            function (error) {
                callback('connect'); // assume we haven't connected by default
            },
            {
                address: this_._active_address
            }
        );
    },

    _clear_connect_timeout: function () {
        if (this._connect_timeout_id) {
            clearTimeout(this._connect_timeout_id);
            this._connect_timeout_id = null;
        }
    },

    _start_connect_timeout: function () {
        var this_ = this;
        this._clear_connect_timeout();
        this._connect_timeout_id = setTimeout(function () {
            var on_close_finished = function () {
                this_.on_device_connection_failure(BluetoothErrorCode.ERROR_FAILURE_TO_CONNECT);
            };

            CordovaPlugins().bluetoothle.close(on_close_finished, on_close_finished, {
                address: this_._active_address
            });
        }, this.CONNECT_TIMEOUT_MS);
    },

    _connect: function (address) {
        var this_ = this;
        this_._active_address = address;
        this_._get_connect_fn(function (connect_fn) {
            this_._start_connect_timeout();

            CordovaPlugins().bluetoothle[connect_fn](
                function (response) {
                    this_._clear_connect_timeout();

                    if (response.status !== 'connected') {
                        // @author:david from the BLE docs: "The original connection callback will be called again and receive
                        // an object with status => disconnected."
                        return this_.on_device_disconnected(
                            BluetoothErrorCode.ERROR_FAILURE_TO_CONNECT,
                            'Status reports not connected'
                        );
                    }

                    this_.on_device_connected();
                },
                function (error) {
                    this_._clear_connect_timeout();

                    if (error.error === 'enable') {
                        return this_.on_device_connection_failure(BluetoothErrorCode.ERROR_BLUETOOTH_DISABLED);
                    }

                    // isNotDisconnected is returned as an error after a previous failed connection, so let's ignore this
                    // Not a concern since we don't allow multiple connections (it's ignored in $f.mobile.bluetooth) so don't worry about the other case
                    // (we're already connected)
                    if (error.error === 'isNotDisconnected') {
                        return this_.on_device_connection_failure(BluetoothErrorCode.ERROR_FAILURE_TO_CONNECT);
                    }

                    this_.on_device_connection_failure(BluetoothErrorCode.ERROR_FAILURE_TO_CONNECT);
                },
                {
                    address: this_._active_address,
                    autoConnect: false
                }
            );
        });
    },

    /**
     * Determine if we should call connect() or reconnect()
     */
    connect: function (address) {
        this.on_pre_connect();
        this._connect(address);
    },

    _close: function () {
        var this_ = this;

        CordovaPlugins().bluetoothle.close(
            function (response) {
                this_.on_device_disconnected(BluetoothErrorCode.ERROR_DISCONNECTION_SUCCESSFUL);
            },
            function (error) {
                $f.mobile.logging.log_error('Failed to close bluetoothle', { message: error.message }); // maybe we were never connected
                this_.on_device_disconnected(BluetoothErrorCode.ERROR_DISCONNECTION_SUCCESSFUL);
            },
            {
                address: this_._active_address
            }
        );
    },

    disconnect: function () {
        // Stop scanning if necessary before disconnecting
        if ($f.mobile.bluetooth.is_scanning) {
            $f.mobile.bluetooth.stop_scan(this.disconnect.bind(this));
            return;
        }

        // We got what we came for, let's disconnect.
        CordovaPlugins().bluetoothle.disconnect(
            () => {
                this._close();
            },
            () => {
                this._close('Failed to disconnect bluetoothle');
            },
            {
                address: this._active_address
            }
        );
    },

    is_device_connected: function (callback) {
        CordovaPlugins().bluetoothle.isConnected(
            function (answer) {
                callback(true);
            },
            function (response) {
                callback(false);
            },
            {
                address: this._active_address
            }
        );
    },

    // poll for temperatures
    start_polling: function (options) {
        options = options || {};
        options.error = options.error || function () {};
        var this_ = this;

        if (options.success) {
            throw new Error(
                'Programmer Error: unexpected success callback in start_polling(). Use on_reading_received()'
            );
        }

        setTimeout(function () {
            // @author:david waiting a little bit helps show services?
            CordovaPlugins().bluetoothle.isDiscovered(
                function (response) {
                    if (response.isDiscovered) {
                        return this_.subscribe(options);
                    }

                    // We need to call discover() first so BLE knows what characteristics we can look for

                    CordovaPlugins().bluetoothle.discover(
                        function () {
                            this_.subscribe(options);
                        },
                        function (error) {
                            options.error(BluetoothErrorCode.ERROR_FAILURE_TO_POLL, error.message);
                        },
                        {
                            address: this_._active_address
                        }
                    );
                },
                function (error) {
                    options.error(BluetoothErrorCode.ERROR_FAILURE_TO_POLL, error.message);
                },
                {
                    address: this_._active_address,
                    clearCache: true
                }
            );
        }, this.DISCOVERY_DELAY_MS);
    },

    subscribe: function (options) {
        throw new Error('Programmer Error: subscribe() not implemented');
    },

    on_failure_to_subscribe: function (ble_error, error_callback) {
        if (ble_error.message.toLowerCase().indexOf('already subscribed') > -1) {
            return; // this can happen when the user tries to poll multiple fields at once. Nothing to worry about.
        }

        // TempAlert will ask iOS to pair because "Authentication is insufficient" (Code=15)
        const errCode = ble_error.message.includes('Code=15')
            ? BluetoothErrorCode
            : BluetoothErrorCode.ERROR_FAILURE_TO_POLL;

        error_callback(errCode, ble_error.message);
    },

    stop_polling: function () {
        var this_ = this;

        if (this.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_NOTIF) {
            this.unsubscribe_from_temperature_characteristic(this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_NOTIF);
        }

        this.unsubscribe_from_temperature_characteristic(this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC);
    },

    unsubscribe_from_temperature_characteristic: function (temp_characteristic) {
        var this_ = this;

        CordovaPlugins().bluetoothle.unsubscribe(this.on_success_to_unsubscribe, this.on_failure_to_unsubscribe, {
            address: this_._active_address,
            service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
            characteristic: temp_characteristic
        });
    },

    on_success_to_unsubscribe: function (response) {
        if (response.status === 'unsubscribed') {
            return;
        }

        this.on_failure_to_unsubscribe({
            message: 'Response status does not match unsubscribed'
        });
    },

    on_failure_to_unsubscribe: function (error) {
        console.error('Unable to unsubscribe');
    },

    convert_base64_to_string: function (value) {
        try {
            // Strings are base64 encoded. See bluetoothle docs here:
            // https://github.com/randdusing/cordova-plugin-bluetoothle#encodedstringtobytes
            var string_value = atob(value).trim();
            var numeric_value = parseFloat(string_value.match(/((\-)??[0-9]*(\.)??[0-9]*)+/));
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data', {
                probe_type: $f.mobile.bluetooth.device.get('type'),
                response_value: value
            });

            return null;
        }

        if (!numeric_value || isNaN(numeric_value)) {
            return null;
        }

        // convert
        if (string_value.indexOf('C') > -1) {
            numeric_value = $f.utils.centigrade_to_fahrenheit(numeric_value, 2);
        }

        return numeric_value;
    }
});

export const BluetoothDevice = Backbone.Model.extend({
    defaults: {
        name: '(unknown)',
        address: '',
        selected: false,
        type: ''
    }
});

export const BluetoothDevices = Backbone.Collection.extend({
    selected_device: null,

    select_device: function (device) {
        this.selected_device = device;
    },

    unselect_device: function () {
        this.selected_device = null;
    }
});

export const CooperAtkinsAPI = BluetoothDeviceAPI.extend({
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: 'F2B32C77-EA68-464B-9CD7-A22CBFFB98BD',
        BLE_TEMPERATURE_CHARACTERISTIC: '78544003-4394-4FC2-8CFD-BE6A00AA701B',
        BLE_SLEEP_TIME: 'FB441C3A-87FC-477D-9F53-7F1C53F0BB03'
    },

    STATUS: {
        WRITTEN: 'written',
        SUBSCRIBED: 'subscribed'
    },

    SLEEP_TIME_STRING_5: '5',

    parse_ble_response_to_fahrenheit: function (response_value) {
        return this.convert_base64_to_string(response_value);
    },

    did_ble_button_press: function (response_value) {
        try {
            // Strings are base64 encoded. See bluetoothle docs here:
            // https://github.com/randdusing/cordova-plugin-bluetoothle#encodedstringtobytes
            var string_value = atob(response_value).trim();
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data', {
                probe_type: 'cooperatkins',
                response_value: response_value
            });
            return false;
        }

        return string_value.indexOf('S') > -1;
    },

    subscribe: function (options) {
        var this_ = this;
        options = options || {};
        options.error = options.error || function () {};

        this._write_sleep_time({
            success: function () {
                this_._subscribe(options.error);
            }
        });
    },

    _write_sleep_time: function (options) {
        var this_ = this;
        options = options || {};
        options.error = options.error || function () {};

        if (!options.success) {
            throw new Error('Programmer Error: missing success callback in _write_sleep_time()');
        }

        CordovaPlugins().bluetoothle.write(
            function (response) {
                if (response.status === this_.STATUS.WRITTEN) {
                    options.success();
                }
            },
            function (error) {
                options.error(error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_SLEEP_TIME,
                value: this_._get_encoded_sleep_time()
            }
        );
    },

    _get_encoded_sleep_time: function () {
        var sleep_time_bytes = CordovaPlugins().bluetoothle.stringToBytes(this.SLEEP_TIME_STRING_5);
        return CordovaPlugins().bluetoothle.bytesToEncodedString(sleep_time_bytes);
    },

    _subscribe: function (error) {
        var this_ = this;
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === this_.STATUS.SUBSCRIBED) {
                    return;
                }
                if (!response.value) {
                    return;
                }

                var reading = this_.parse_ble_response_to_fahrenheit(response.value);

                if (!reading) {
                    return;
                }

                if (this_.did_ble_button_press(response.value)) {
                    this_.on_button_pressed(reading);
                    return;
                }

                this_.on_reading_received(reading);
            },
            function (_error) {
                this_.on_failure_to_subscribe(_error, error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC
            }
        );
    }
});

export const NordicAPI = BluetoothDeviceAPI.extend({
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: '6E400001-B5A3-F393-E0A9-E50E24DCCA9E',
        BLE_SLEEP_TIME: '6E400002-B5A3-F393-E0A9-E50E24DCCA9E',
        BLE_TEMPERATURE_CHARACTERISTIC: '6E400003-B5A3-F393-E0A9-E50E24DCCA9E'
    },

    STATUS: {
        WRITTEN: 'written',
        SUBSCRIBED: 'subscribed'
    },

    parse_ble_response_to_fahrenheit: function (response_value) {
        try {
            // The deprecation suggestion is misleading here.
            // Do not use Buffer in this code, this is not a node environment
            var string_value = atob(response_value.trim()).trim();
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature buffer data', {
                probe_type: $f.mobile.bluetooth.device.get('type'),
                response_value: response_value
            });

            return null;
        }
        try {
            var numeric_value = parseFloat(string_value.match(/(\-)?[0-9]+\.[0-9]+/));
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature numeric data', {
                probe_type: $f.mobile.bluetooth.device.get('type'),
                response_value: response_value
            });

            return null;
        }

        if (!numeric_value || isNaN(numeric_value)) {
            return null;
        }

        // convert
        // Readings are starting with TCT
        if (string_value.replace('TCT', '').indexOf('C') > -1) {
            numeric_value = $f.utils.centigrade_to_fahrenheit(numeric_value, 2);
        }

        return numeric_value;
    },

    did_ble_button_press: function (response_value) {
        try {
            // The deprecation suggestion is misleading here.
            // Do not use Buffer in this code, this is not a node environment
            var string_value = atob(response_value.trim()).trim();
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data for button press', {
                probe_type: 'nordic',
                response_value: response_value
            });
            return false;
        }

        return string_value.indexOf('R') > -1;
    },

    subscribe: function (error) {
        var this_ = this;
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === this_.STATUS.SUBSCRIBED) {
                    return;
                }
                if (!response.value) {
                    return;
                }

                var reading = this_.parse_ble_response_to_fahrenheit(response.value);

                if (!reading) {
                    return;
                }

                if (this_.did_ble_button_press(response.value)) {
                    this_.on_button_pressed(reading);
                    return;
                }

                this_.on_reading_received(reading);
            },
            function (_error) {
                this_.on_failure_to_subscribe(_error, error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC
            }
        );
    }
});

export const BluethermBLEAPI = BluetoothDeviceAPI.extend({
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: '45544942-4C55-4554-4845-524DB87AD700',
        BLE_TEMPERATURE_CHARACTERISTIC: '45544942-4C55-4554-4845-524DB87AD701',
        BLE_TEMPERATURE_CHARACTERISTIC_NOTIF: '45544942-4C55-4554-4845-524DB87AD705'
    },

    parse_ble_response_to_fahrenheit: function (response_value) {
        try {
            // get value as Little Endian, which is what Bluetherm sends
            var val = new DataView(CordovaPlugins().bluetoothle.encodedStringToBytes(response_value).buffer).getFloat32(
                null,
                true
            );
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data', {
                probe_type: 'bluetherm',
                response_value: response_value
            });
            return null; // failed, probably some corrupt value
        }

        if (isNaN(val)) {
            return null;
        }

        return $f.utils.centigrade_to_fahrenheit(val, 2);
    },

    did_ble_button_press: function (response_value) {
        try {
            // get value as Little Endian, which is what Bluetherm sends
            var val = new DataView(CordovaPlugins().bluetoothle.encodedStringToBytes(response_value).buffer).getUint16(
                null,
                true
            );
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data', {
                probe_type: 'bluetherm',
                response_value: response_value
            });
            return false; // failed, probably some corrupt value
        }

        return val === 1; // 1 means button press
    },

    subscribe: function (options) {
        var this_ = this;

        options = options || {};
        options.error = options.error || function () {};

        var last_received_value = null;

        // Subscribe to button press notifications
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === 'subscribed') {
                    return;
                }

                if (!response.value) {
                    return;
                }

                if (!this_.did_ble_button_press(response.value) || !last_received_value) {
                    return;
                }

                this_.on_button_pressed(last_received_value);
            },
            function (error) {
                this_.on_failure_to_subscribe(error, options.error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_NOTIF
            }
        );

        // Subscribe to temperature polling
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === 'subscribed') {
                    return;
                }
                if (!response.value) {
                    return;
                }

                var reading = this_.parse_ble_response_to_fahrenheit(response.value);

                if (!reading) {
                    return;
                }

                last_received_value = reading;
                this_.on_reading_received(reading);
            },
            function (error) {
                this_.on_failure_to_subscribe(error, options.error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC
            }
        );
    }
});

export const TempAlertAPI = BluetoothDeviceAPI.extend({
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: '9D55A000-BC06-40EC-94CB-266609000419',
        BLE_TEMPERATURE_CHARACTERISTIC: '9D55C000-BC06-40EC-94CB-266609000419'
    },

    /**
     * This always assumes a decimal places of 2n
     *
     * @param {number} hex_str
     */
    _get_decimal_from_hex: function (hex_str) {
        hex_str = hex_str.replace(/0x/g, ''); // remove '0x'
        var hex_arr = hex_str.split(' ');
        hex_arr.reverse(); // because data sent is LSB

        var ordered_hex_str = hex_arr.join('');

        return this._hex_to_signed_int(ordered_hex_str) * Math.pow(10, -2);
    },
    /**
     * This converts a hex number to a sgined integer
     * Hex conversion depends on the hex's length to determine if it's positive or negative

     * @param {String} hex_str
     * @returns {Number}
     * source: https://stackoverflow.com/a/34679269/2603945
     */
    _hex_to_signed_int: function (hex) {
        // if the length of the hex is not multiples of two, pad with zero
        if (hex.length % 2 != 0) {
            hex = '0' + hex;
        }

        var decimal_number = parseInt(hex, 16);
        var max_value_for_hex_length = Math.pow(2, (hex.length / 2) * 8);

        // if the decimal number is greater than half of the max value, it's a negative number
        if (decimal_number > max_value_for_hex_length / 2 - 1) {
            decimal_number = decimal_number - max_value_for_hex_length;
        }

        return decimal_number;
    },

    /* break down of the device value and what we do with it
        06 fd fe ff fe 00 00 00 00 00 0b 29 00
        The first two bits of the device reading represent status of the tempe type and time stamp, therefore excluded
        06 = temp type and time stamp status flag( defaults to enabled)
        temp = fffefd // because LSB first
        -2 (100th of a degree): FE
        timestamp = 00 00 00 00 00 0b 29
        temp type = 00
    */
    parse_ble_response_to_fahrenheit: function (response_value) {
        try {
            var bytes = CordovaPlugins().bluetoothle.encodedStringToBytes(response_value);
            var temperature_bytes = bytes.slice(1, 4);
            var hex = CordovaPlugins().bluetoothle.bytesToHex(temperature_bytes);
            var val = this._get_decimal_from_hex(hex);
        } catch (e) {
            $f.mobile.logging.log_error('Unable to parse BLE temperature data', {
                probe_type: 'tempalert',
                response_value: response_value
            });
            return null; // failed, probably some corrupt value
        }

        if (isNaN(val)) {
            return null;
        }

        return $f.utils.centigrade_to_fahrenheit(val, 2);
    },

    subscribe: function (options) {
        var this_ = this;

        options = options || {};
        options.error = options.error || function () {};

        // Subscribe to temperature polling
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === 'subscribed') {
                    return;
                }
                if (!response.value) {
                    return;
                }

                var reading = this_.parse_ble_response_to_fahrenheit(response.value);

                if (!reading) {
                    return;
                }

                this_.on_reading_received(reading);
            },
            function (error) {
                this_.on_failure_to_subscribe(error, options.error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: this_.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC
            }
        );
    }
});

export const TestoAPI = BluetoothDeviceAPI.extend({
    UUIDS: {
        BLE_TEMPERATURE_SERVICE: '00005A90-3354-11E8-B467-0ED5F89F718B',
        BLE_TEMPERATURE_CHARACTERISTIC_PLUNGE: '00005A93-3354-11E8-B467-0ED5F89F718B', // plunge temperature
        BLE_TEMPERATURE_CHARACTERISTIC_SURFACE: '00005A92-3354-11E8-B467-0ED5F89F718B' // surface temperature
    },

    parse_ble_response_to_fahrenheit: function (response_value) {
        return this.convert_base64_to_string(response_value);
    },

    subscribe: function (options) {
        options = options || {};
        options.error = options.error || function () {};

        this._subscribe(
            $.extend(options, {
                BLE_TEMPERATURE_CHARACTERISTIC: this.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_PLUNGE
            })
        );
        this._subscribe(
            $.extend(options, {
                BLE_TEMPERATURE_CHARACTERISTIC: this.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_SURFACE
            })
        );
    },

    stop_polling: function () {
        this.unsubscribe_from_temperature_characteristic(this.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_PLUNGE);
        this.unsubscribe_from_temperature_characteristic(this.UUIDS.BLE_TEMPERATURE_CHARACTERISTIC_SURFACE);
    },

    _subscribe: function (options) {
        var this_ = this;

        // Subscribe to temperature polling
        CordovaPlugins().bluetoothle.subscribe(
            function (response) {
                if (response.status === 'subscribed') {
                    return;
                }
                if (!response.value) {
                    return;
                }

                var reading = this_.parse_ble_response_to_fahrenheit(response.value);

                if (!reading) {
                    return;
                }

                this_.on_button_pressed(reading);
            },
            function (error) {
                this_.on_failure_to_subscribe(error, options.error);
            },
            {
                address: this_._active_address,
                service: this_.UUIDS.BLE_TEMPERATURE_SERVICE,
                characteristic: options.BLE_TEMPERATURE_CHARACTERISTIC
            }
        );
    }
});
